import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import Footer from "@/components/layout/Footer";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common";
import { EventPeopleStrip } from "@/components/events/EventPeople";
import Countdown from "@/components/landing/Countdown";
import AttendButton from "@/components/landing/AttendButton";
import VenueCard from "@/components/landing/VenueCard";
import ThemeCard from "@/components/landing/ThemeCard";
import EventCard from "@/components/landing/EventCard";
import LiveShareBar from "@/components/landing/LiveShareBar";
import ReplaySection from "@/components/landing/ReplaySection";
import { directionsUrl, fullAddress, rhythmAccentStyle, ordinalWord, eventHasEnded, mapEmbedSrc, hoursSummary, scheduleExceptions, locTimes, timeRange, clockWord, shiftHours } from "@/lib/fridays";
import { nextFriday, longDate, shortDate, pacificEpoch } from "@/lib/nextFriday";
import { formatTime } from "@/lib/format";
import {
  CalendarDays, MapPin, Mic, ArrowRight, Users, Quote, PlayCircle, ImageIcon, Handshake, Utensils, Coffee,
  Navigation, Sparkles, HeartHandshake, DoorOpen, UserPlus, ChevronRight, Clock, Youtube, ExternalLink,
} from "lucide-react";

const Logo3D = lazy(() => import("@/components/Logo3D"));

const ease = [0.22, 1, 0.36, 1];
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const TIMELINE = [
  { time: "12:00", label: "Noon", title: "Lunch + networking", desc: "Walk in, grab lunch, and meet the room. Someone will say hi within a minute — we make sure of it.", icon: Utensils, testid: "landing-schedule-step-lunch" },
  { time: "1:00", label: "1 PM", title: "The live conversation", desc: "Our live podcast hour on the month's theme — hosts, a guest or two, and questions from the room.", icon: Mic, testid: "landing-schedule-step-podcast" },
  { time: "2:00", label: "2 PM", title: "Overtime", desc: "Feeling it? Stay until 3 and keep the conversation going with the people you just met.", icon: Coffee, testid: "landing-schedule-step-connect" },
];

const ESSENTIALS = [
  { icon: DoorOpen, t: "No RSVP — just show up", d: "Tap “I’ll be there” if you like; it only helps us set the room." },
  { icon: HeartHandshake, t: "Come as you are", d: "Business owners, nonprofit folks, creatives, students. All are welcome." },
  { icon: UserPlus, t: "Bring a friend", d: "The best first Fridays happen with a coworker or neighbor in tow." },
  { icon: Sparkles, t: "One theme, one venue per Friday", d: "Each Friday of the month has its own topic and its own room across Spokane." },
];

const STEPS = [
  { n: "01", t: "Show up Friday", d: "Pick the venue for that week and arrive at its start time — noon most Fridays.", icon: CalendarDays },
  { n: "02", t: "Grab lunch, meet the room", d: "Real conversations, no pitch decks. The theme gives everyone a place to start.", icon: Users },
  { n: "03", t: "Join the network", d: "Members get the directory, the Podcast Hub, clips and referrals all week long.", icon: Handshake },
];

function Section({ id, className = "", children, testId }) {
  return (
    <section id={id} className={`scroll-mt-20 py-16 sm:py-20 lg:py-28 ${className}`} data-testid={testId}>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, subtitle, align = "left" }) {
  return (
    <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>}
      <h2 className="mt-3 font-display text-3xl font-semibold leading-[1.1] tracking-tight text-foreground sm:text-4xl">{title}</h2>
      {subtitle && <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">{subtitle}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero: the next Friday, exactly where to show up                     */
/* ------------------------------------------------------------------ */
function NextFridayPanel({ next, loading, onViewFlyer }) {
  if (loading) return <Skeleton className="h-72 w-full rounded-3xl" />;
  if (!next) {
    return (
      <div className="rounded-3xl border border-border bg-card/80 p-6 backdrop-blur" data-testid="next-friday-hero-card">
        <p className="text-sm text-muted-foreground">Our next Friday is being scheduled — check back soon.</p>
      </div>
    );
  }
  const { date, ordinal, location: loc, event: ev, startTime, endTime, skippedCoreWeek } = next;
  const theme = ev?.theme_title || loc?.theme_title;
  const tagline = loc?.theme_tagline;
  const start = pacificEpoch(date, startTime);
  const end = pacificEpoch(date, endTime);
  return (
    <motion.div variants={fadeUp} style={rhythmAccentStyle(ordinal)} className="h-full">
      <div className="relative h-full overflow-hidden rounded-3xl border border-border bg-card/85 shadow-[0_30px_80px_rgba(92,52,18,0.12)] backdrop-blur-md" data-testid="next-friday-hero-card">
        <div className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: "var(--fc-friday-accent)" }} />
        <div className="p-6 pl-7 sm:p-8 sm:pl-9">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Next Friday</span>
            <span className="rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--fc-friday-accent)", borderColor: "color-mix(in oklab, var(--fc-friday-accent) 40%, transparent)" }} data-testid="next-friday-ordinal">
              {ordinalWord(ordinal)} Friday of the month
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
            <h2 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground sm:text-4xl" data-testid="next-friday-date">{longDate(date)}</h2>
            <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><Clock className="h-4 w-4" /> {formatTime(startTime)} – {formatTime(endTime)} Pacific</p>
          </div>
          {theme && (
            <p className="mt-3 text-lg font-semibold text-foreground" data-testid="next-friday-theme">
              {theme}{tagline && <span className="font-normal text-muted-foreground"> — {tagline}</span>}
            </p>
          )}
          {loc && (
            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-border bg-background/70 p-4" data-testid="next-friday-venue">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: "color-mix(in oklab, var(--fc-friday-accent) 14%, transparent)", color: "var(--fc-friday-accent)" }}><MapPin className="h-4.5 w-4.5" /></span>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{loc.name}{loc.room ? <span className="text-muted-foreground"> · {loc.room}</span> : null}</p>
                <p className="text-sm text-muted-foreground">{fullAddress(loc)}</p>
                {loc.directions?.[0] && <p className="mt-1 text-sm text-muted-foreground"><span className="font-medium text-foreground">Find us:</span> {loc.directions[0]}</p>}
              </div>
            </div>
          )}
          <div className="mt-6">
            <Countdown target={start} endTarget={end} />
          </div>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {loc && (
                <Button asChild size="lg" className="rounded-full bg-primary px-6 text-primary-foreground shadow-[0_10px_30px_rgba(201,119,46,0.35)] hover:bg-[hsl(var(--primary-hover))]" data-testid="get-directions-button">
                  <a href={directionsUrl(loc)} target="_blank" rel="noopener noreferrer"><Navigation className="mr-1.5 h-4 w-4" /> Get directions</a>
                </Button>
              )}
              {ev?.flyer_image && (
                <Button type="button" size="lg" variant="ghost" className="rounded-full" onClick={() => onViewFlyer(ev)} data-testid="landing-this-week-flyer">
                  <ImageIcon className="mr-1.5 h-4 w-4" /> View flyer
                </Button>
              )}
              {ev && (
                <Link to={`/events/${ev.event_id}`} className="inline-flex items-center text-sm font-medium text-primary hover:underline" data-testid="landing-this-week-view-details">Event details <ChevronRight className="h-4 w-4" /></Link>
              )}
            </div>
          </div>
          <div className="mt-5 border-t border-border pt-5">
            <AttendButton date={date} size="default" />
          </div>
          {ev?.people?.length > 0 && <EventPeopleStrip people={ev.people} className="mt-5" />}
          {skippedCoreWeek && (
            <p className="mt-5 rounded-xl bg-secondary px-3.5 py-2.5 text-xs text-muted-foreground" data-testid="core-week-note">
              Heads up: {shortDate(skippedCoreWeek)} is a 5th Friday — that&rsquo;s our Core team week, so there&rsquo;s no public gathering. See you {shortDate(date)}.
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function HeroVisual({ next, heroPhoto, onViewFlyer }) {
  const loc = next?.location;
  return (
    <div className="relative lg:h-full lg:min-h-[520px]">
      <div className="relative h-[380px] overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_40px_100px_rgba(92,52,18,0.18)] sm:h-[460px] lg:absolute lg:inset-0 lg:h-auto" data-testid="landing-hero-visual">
        {heroPhoto ? (
          <>
            <img src={heroPhoto.data || heroPhoto.url} alt={heroPhoto.title || "Friday Conversations gathering"} className="h-full w-full object-cover" data-testid="landing-hero-image" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2B1608]/80 via-[#2B1608]/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F0B57A]">{heroPhoto.title || "A recent Friday"}</p>
              {heroPhoto.description && <p className="mt-1 font-display text-2xl font-semibold leading-tight">{heroPhoto.description}</p>}
            </div>
            <span className="absolute right-4 top-4 rounded-full bg-card/90 px-3 py-1 text-xs font-semibold text-foreground shadow backdrop-blur">No RSVP needed</span>
          </>
        ) : loc ? (
          <div className="relative h-full" data-testid="landing-hero-map" style={rhythmAccentStyle(next.ordinal)}>
            <iframe title={`Map to ${loc.name}`} src={mapEmbedSrc(loc)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-full w-full border-0" />
            <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-2xl border border-border bg-card/95 p-4 shadow-[0_18px_40px_rgba(0,0,0,0.28)] backdrop-blur-md sm:inset-x-5 sm:bottom-5" data-testid="landing-hero-map-caption">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">This is the spot</p>
              <p className="mt-1 font-display text-xl font-semibold leading-tight text-foreground sm:text-2xl">{loc.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{loc.room ? `${loc.room} · ` : ""}{fullAddress(loc)}</p>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center bg-secondary">
            <Logo className="h-40 w-40 opacity-90" />
          </div>
        )}
      </div>
      {next?.event?.flyer_image && (
        <button
          type="button"
          onClick={() => onViewFlyer(next.event)}
          className="group absolute -bottom-6 -left-3 z-10 w-32 overflow-hidden rounded-2xl border-4 border-background shadow-[0_20px_50px_rgba(92,52,18,0.28)] transition-transform duration-300 hover:-translate-y-1 sm:-left-6 sm:w-40"
          aria-label="View this week's flyer"
          data-testid="landing-hero-flyer"
        >
          <img src={next.event.flyer_image} alt={`${next.event.title} flyer`} className="aspect-[4/5] w-full object-cover" />
          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left text-[10px] font-semibold uppercase tracking-wider text-white">This week&rsquo;s flyer</span>
        </button>
      )}
    </div>
  );
}

function Hero({ next, loading, onViewFlyer, heroPhoto, fridays = [] }) {
  const art = next?.location?.theme_art;
  const exceptions = scheduleExceptions(fridays);
  return (
    <div className="fc-hero-bg fc-paper-noise relative overflow-hidden" id="next">
      {art && (
        <div className="pointer-events-none absolute inset-0" data-testid="hero-theme-backdrop" aria-hidden="true">
          <img src={art} alt="" className="h-full w-full object-cover opacity-[0.16] dark:opacity-[0.12]" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/50 to-background" />
        </div>
      )}
      <div className="relative mx-auto w-full max-w-6xl px-4 pb-16 pt-8 sm:px-6 lg:px-8 lg:pb-24 lg:pt-12">
        {/* Row 1 — headline + 3D mark */}
        <div className="grid items-center gap-6 lg:grid-cols-12 lg:gap-12">
          <motion.div initial="hidden" animate="show" variants={stagger} className="lg:col-span-7">
            <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3.5 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-primary" /> <span data-testid="hero-hours">Every Friday · {hoursSummary(fridays)} · Spokane, WA</span>
            </motion.span>
            <motion.h1 variants={fadeUp} className="mt-6 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="landing-hero-title">
              Show up Friday.<br /><span className="fc-copper-gradient">Leave connected.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 max-w-[56ch] text-base leading-7 text-muted-foreground sm:text-lg">
              A weekly Spokane gathering built on real conversations: lunch at noon, a live podcast hour at one,
              and people worth meeting. Different theme and venue each Friday of the month — same warm room.
              {exceptions.length > 0 && (
                <span className="mt-2 block text-sm font-medium text-foreground" data-testid="hero-hours-note">
                  {exceptions.map((l) => `${ordinalWord(l.friday_ordinal)} Friday at ${l.name.split(" — ")[0]} runs ${timeRange(locTimes(l).start, locTimes(l).end)}`).join("; ")}.
                </span>
              )}
            </motion.p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease, delay: 0.1 }} className="flex justify-center lg:col-span-5 lg:justify-end" data-testid="hero-logo-slot">
            <Suspense fallback={<div className="flex h-56 w-56 items-center justify-center sm:h-64 sm:w-64"><Logo className="h-32 w-32 animate-pulse" /></div>}>
              <Logo3D className="h-56 w-56 sm:h-64 sm:w-64 lg:h-[300px] lg:w-full" />
            </Suspense>
          </motion.div>
        </div>

        {/* Row 2 — Next Friday card + map, top-aligned */}
        <div className="mt-8 grid items-stretch gap-8 lg:mt-10 lg:grid-cols-12 lg:gap-12">
          <motion.div initial="hidden" animate="show" variants={stagger} className="lg:col-span-7">
            <NextFridayPanel next={next} loading={loading} onViewFlyer={onViewFlyer} />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.2 }} className="lg:col-span-5">
            <HeroVisual next={next} heroPhoto={heroPhoto} onViewFlyer={onViewFlyer} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Your first Friday                                                    */
/* ------------------------------------------------------------------ */
function FirstFriday({ photo, fridays = [] }) {
  const exceptions = scheduleExceptions(fridays);
  return (
    <Section id="first-friday" className="bg-card" testId="first-friday-section">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Your first Friday" title="Here’s exactly how it goes." subtitle="Three hours, one rhythm — the theme and the room change each Friday of the month, the flow never does." />
          {photo ? (
            <div className="relative mt-10 overflow-hidden rounded-[2rem] border border-border shadow-[0_30px_80px_rgba(92,52,18,0.14)]">
              <img src={photo.data || photo.url} alt={photo.title || "A Friday Conversations gathering"} loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="first-friday-photo" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#2B1608]/75 to-transparent" />
              {(photo.title || photo.description) && <p className="absolute bottom-4 left-5 right-5 text-sm font-semibold text-white">{photo.description || photo.title}</p>}
            </div>
          ) : (
            <div className="mt-10 rounded-[2rem] border border-border bg-background p-6" data-testid="first-friday-glance">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">A Friday at a glance</p>
              <div className="mt-4 flex items-end gap-2 font-display text-5xl font-semibold leading-none text-foreground">12<span className="text-2xl text-muted-foreground">PM</span><span className="mx-1 text-3xl text-primary">→</span>3<span className="text-2xl text-muted-foreground">PM</span></div>
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Utensils className="h-4 w-4 text-primary" /> Lunch &amp; networking from noon</li>
                <li className="flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> Live podcast hour at 1</li>
                <li className="flex items-center gap-2"><Coffee className="h-4 w-4 text-primary" /> Overtime until 3</li>
              </ul>
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          <ol className="relative space-y-6 border-l-2 border-border pl-8 sm:pl-10" data-testid="first-timers-timeline">
            {TIMELINE.map((s, i) => (
              <motion.li key={s.title} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, ease, delay: i * 0.08 }} className="relative" data-testid={s.testid}>
                <span className="absolute -left-[2.55rem] top-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary bg-background text-primary sm:-left-[3.05rem]">
                  <s.icon className="h-4 w-4" />
                </span>
                <div className="rounded-2xl border border-border bg-background p-5 transition-shadow hover:shadow-[0_18px_50px_rgba(92,52,18,0.10)]">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 font-display text-sm font-semibold tabular-nums text-primary">{s.label}</span>
                    <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{s.desc}</p>
                </div>
              </motion.li>
            ))}
          </ol>
          {exceptions.length > 0 && (
            <div className="mt-6 flex gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="schedule-exception-note">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="text-sm leading-6 text-foreground">
                {exceptions.map((l) => {
                  const { start, end } = locTimes(l);
                  return (
                    <p key={l.location_id}>
                      <span className="font-semibold">Heads up — the {ordinalWord(l.friday_ordinal)} Friday at {l.name.split(" — ")[0]} runs {timeRange(start, end)}.</span>{" "}
                      Same rhythm, later start: doors and networking at {clockWord(start)}, the live conversation at {clockWord(shiftHours(start, 1))}, Overtime until {clockWord(end)}.
                    </p>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="first-timer-essentials">
            {ESSENTIALS.map((e, i) => (
              <motion.div key={e.t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.35, ease, delay: i * 0.05 }} className="flex gap-3.5 rounded-2xl border border-border bg-background p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary"><e.icon className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{e.t}</p>
                  <p className="mt-0.5 text-sm leading-5 text-muted-foreground">{e.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function ThemeArtFan({ fridays }) {
  const art = fridays.filter((l) => l.theme_art).slice(0, 4);
  if (art.length === 0) return null;
  const tilt = ["-rotate-3", "rotate-2", "rotate-1", "-rotate-2"];
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="join-theme-art-fan">
      {art.map((l, i) => (
        <motion.figure key={l.location_id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.45, ease, delay: i * 0.08 }} className={`${tilt[i]} overflow-hidden rounded-2xl border-4 border-card bg-card shadow-[0_18px_40px_rgba(92,52,18,0.16)] transition-transform duration-300 hover:rotate-0 hover:scale-[1.03]`}>
          <img src={l.theme_art} alt={`${l.theme_title} theme art`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
          <figcaption className="px-3 py-2 text-xs font-semibold text-foreground"><span className="text-primary">{ordinalWord(l.friday_ordinal)} Friday</span> · {l.theme_title}</figcaption>
        </motion.figure>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Join band                                                            */
/* ------------------------------------------------------------------ */
function JoinBand({ fridays }) {
  return (
    <section id="join" className="fc-paper-noise relative overflow-hidden bg-secondary/70 text-foreground" data-testid="join-band">
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, hsl(var(--brand-copper) / 0.28), transparent 42%), radial-gradient(circle at 10% 90%, hsl(var(--brand-copper-2) / 0.18), transparent 40%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(hsl(var(--brand-copper)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--brand-copper)) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="relative mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">How it works</p>
            <h2 className="mt-3 font-display text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl">Visiting is easy. Belonging is even better.</h2>
            <ol className="mt-10 grid gap-6 sm:grid-cols-3">
              {STEPS.map((s) => (
                <li key={s.n} className="rounded-2xl border border-border bg-card/80 p-5 backdrop-blur-sm transition-shadow hover:shadow-[0_18px_40px_rgba(92,52,18,0.12)]" data-testid={`how-step-${s.n}`}>
                  <div className="flex items-center justify-between">
                    <s.icon className="h-5 w-5 text-primary" />
                    <span className="font-display text-2xl text-primary/40">{s.n}</span>
                  </div>
                  <h3 className="mt-4 font-semibold">{s.t}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{s.d}</p>
                </li>
              ))}
            </ol>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-primary px-7 text-primary-foreground shadow-[0_10px_30px_rgba(201,119,46,0.35)] hover:bg-[hsl(var(--primary-hover))]" data-testid="landing-join-cta">
                <Link to="/register">Join the group <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full text-foreground hover:bg-foreground/5" data-testid="member-login-button">
                <Link to="/login">Member sign in</Link>
              </Button>
            </div>
          </div>
          <div className="hidden lg:col-span-5 lg:block">
            <ThemeArtFan fridays={fridays} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function Landing() {
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [fridays, setFridays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [live, setLive] = useState(null);
  const [replay, setReplay] = useState(null);
  const [streamVideos, setStreamVideos] = useState([]);
  const [videoModal, setVideoModal] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    const poll = () => api.get("/hub/live").then((r) => { if (active) { setLive(r.data?.live ? r.data : null); setReplay(r.data?.live ? null : (r.data?.replay || null)); } }).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/events", { params: { upcoming: false } }).then((r) => r.data).catch(() => []),
      api.get("/media").then((r) => r.data).catch(() => []),
      api.get("/locations").then((r) => r.data).catch(() => []),
      api.get("/stream/public/videos").then((r) => r.data).catch(() => []),
    ]).then(([ev, md, loc, sv]) => {
      setEvents(ev);
      setMedia(md);
      setStreamVideos(sv || []);
      setFridays((loc || []).filter((l) => [1, 2, 3, 4].includes(l.friday_ordinal)).sort((a, b) => a.friday_ordinal - b.friday_ordinal));
      setLoading(false);
    });
  }, []);

  const upcomingEvents = useMemo(() => events.filter((e) => !eventHasEnded(e)), [events, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const next = useMemo(() => (loading ? null : nextFriday({ events: upcomingEvents, locations: fridays })), [loading, upcomingEvents, fridays]);
  const featured = upcomingEvents[0];
  const openFlyer = (ev) => ev?.flyer_image && setLightbox({ url: ev.flyer_image, title: `${ev.title} — flyer` });

  const videos = [
    ...streamVideos.map((v) => ({ key: `s_${v.video_id}`, title: v.title, description: v.description, iframe: v.iframe, thumbnail: v.thumbnail })),
    ...media.filter((m) => m.type === "video").map((m) => ({ key: `m_${m.media_id}`, title: m.title, description: m.description, iframe: m.url, thumbnail: null })),
  ];
  const photos = media.filter((m) => m.type === "photo");
  const heroPhoto = photos.find((m) => m.placement === "hero") || null;
  const firstFridayPhoto = photos.find((m) => m.placement === "first_friday") || null;

  return (
    <div className="fc-warm min-h-screen bg-background">
      <PublicHeader />

      {live && (
        live.live_video?.iframe ? (
          <a href="#live-now" className="block bg-primary text-primary-foreground" data-testid="landing-live-banner">
            <div className="mx-auto flex w-full max-w-6xl items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium sm:px-6">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>
              We&rsquo;re live now: <span className="font-semibold underline underline-offset-2">{live.session.title}</span> &mdash; watch below
            </div>
          </a>
        ) : (
          <Link to={`/portal/hub/${live.session.session_id}`} className="block bg-primary text-primary-foreground" data-testid="landing-live-banner">
            <div className="mx-auto flex w-full max-w-6xl items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium sm:px-6">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>
              We&rsquo;re live now: <span className="font-semibold underline underline-offset-2">{live.session.title}</span> &mdash; join the Podcast Hub
            </div>
          </Link>
        )
      )}

      {live && live.live_video?.iframe && (
        <section id="live-now" className="scroll-mt-24 border-b border-border bg-card" data-testid="landing-live-section">
          <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-foreground">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-white" /></span>
                Live now
              </span>
              <h2 className="font-display text-xl font-semibold text-foreground sm:text-2xl" data-testid="landing-live-title">{live.session.title}</h2>
              {live.watch_links?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:ml-auto" data-testid="landing-live-watch-links">
                  <span className="text-xs font-medium text-muted-foreground">Also watching on</span>
                  {live.watch_links.map((w) => (
                    <a key={w.url} href={w.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary hover:text-primary" data-testid={`landing-live-watch-${w.platform}`}>
                      {w.platform === "youtube" ? <Youtube className="h-4 w-4 text-[#FF0000]" /> : <ExternalLink className="h-4 w-4" />}
                      Watch on {w.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-black shadow-lg">
              <AspectRatio ratio={16 / 9}>
                <iframe src={live.live_video.iframe} title={live.session.title || "Live stream"} className="h-full w-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen data-testid="landing-live-player" />
              </AspectRatio>
            </div>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Watching live. <Link to={`/portal/hub/${live.session.session_id}`} className="font-medium text-primary hover:underline" data-testid="landing-live-join">Members can open the Podcast Hub</Link> to chat and ask questions.
              </p>
              <LiveShareBar title={live.session.title} testPrefix="landing-live-share" />
            </div>
          </div>
        </section>
      )}

      <Hero next={next} loading={loading} onViewFlyer={openFlyer} heroPhoto={heroPhoto} fridays={fridays} />

      {!live && replay && <ReplaySection replay={replay} />}

      <FirstFriday photo={firstFridayPhoto} fridays={fridays} />

      {/* WHERE WE MEET */}
      <Section id="venues" testId="venues-section">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Where we meet" title="Four Fridays, four rooms. Here’s exactly where." subtitle="The same rotation every month, with the door, the floor and the parking spelled out — so your first visit feels like your fifth." />
          {next?.location && (
            <p className="text-sm text-muted-foreground lg:max-w-xs lg:text-right">
              Next up: <span className="font-semibold text-foreground">{next.location.name}</span> on {shortDate(next.date)}.
            </p>
          )}
        </div>
        {loading ? (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[520px] w-full rounded-3xl" />)}</div>
        ) : fridays.length > 0 ? (
          <div className="mt-10 grid gap-6 lg:grid-cols-2" data-testid="landing-locations-grid">
            {fridays.map((loc, i) => (
              <VenueCard key={loc.location_id} loc={loc} index={i} nextDate={next?.location?.location_id === loc.location_id ? next.date : null} />
            ))}
          </div>
        ) : (
          <EmptyState icon={MapPin} title="Locations coming soon" description="Our rotating Friday venues will appear here." />
        )}
        <p className="mt-8 text-sm text-muted-foreground" data-testid="fifth-friday-note">
          On a fifth Friday of the month there&rsquo;s no public gathering — that time is reserved for our Core team.
        </p>
      </Section>

      {/* WHAT WE TALK ABOUT */}
      <Section id="topics" className="bg-card" testId="topics-section">
        <SectionHeading eyebrow="What we talk about" title="One theme per Friday. Real conversations, not small talk." subtitle="Every Friday of the month carries its own topic — and the live podcast hour digs into it with hosts, guests and questions from the room." />
        {loading ? (
          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-72 w-full rounded-3xl" />)}</div>
        ) : fridays.length > 0 ? (
          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4" data-testid="landing-friday-rhythm-grid">
            {fridays.map((loc, i) => <ThemeCard key={loc.location_id} loc={loc} index={i} isNext={next?.ordinal === loc.friday_ordinal} />)}
          </div>
        ) : (
          <p className="mt-8 text-muted-foreground">Theme details are coming soon.</p>
        )}
        {featured?.theme_description && (
          <motion.blockquote initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="fc-section-accent mx-auto mt-12 max-w-3xl rounded-3xl border border-border p-8 text-center sm:p-10" data-testid="this-week-theme">
            <Quote className="mx-auto h-7 w-7 text-primary" />
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary">This week&rsquo;s theme</p>
            <p className="mt-2 font-display text-2xl font-semibold text-foreground sm:text-3xl">{featured.theme_title}</p>
            <p className="mx-auto mt-3 max-w-[60ch] text-base leading-7 text-muted-foreground">{featured.theme_description}</p>
          </motion.blockquote>
        )}
      </Section>

      {/* UPCOMING EVENTS */}
      <Section id="events" testId="events-section">
        <SectionHeading eyebrow="On the calendar" title="Upcoming Fridays" subtitle="Everyone is welcome. Tap a flyer to share it with someone who should be there." />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="upcoming-events-grid">
          {loading ? (
            [0, 1, 2].map((i) => <Skeleton key={i} className="h-96 w-full rounded-3xl" />)
          ) : upcomingEvents.length === 0 ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <EmptyState icon={CalendarDays} title="No events posted yet" description="Friday Conversations happen every week — dated events with flyers and guests are posted here." />
            </div>
          ) : (
            upcomingEvents.map((ev, i) => <EventCard key={ev.event_id} ev={ev} index={i} onViewFlyer={openFlyer} />)
          )}
        </div>
      </Section>

      {/* HIGHLIGHTS */}
      {(videos.length > 0 || photos.length > 0) && (
        <Section id="highlights" className="bg-card" testId="highlights-section">
          <SectionHeading eyebrow="Highlights" title="Catch up on what you missed" subtitle="Episodes, clips and photos from recent Fridays." />
          <Tabs defaultValue={videos.length > 0 ? "videos" : "photos"} className="mt-10">
            <TabsList data-testid="landing-media-tabs">
              <TabsTrigger value="videos"><PlayCircle className="mr-2 h-4 w-4" />Videos</TabsTrigger>
              <TabsTrigger value="photos"><ImageIcon className="mr-2 h-4 w-4" />Photos</TabsTrigger>
            </TabsList>
            <TabsContent value="videos" className="mt-6">
              {videos.length === 0 ? (
                <EmptyState icon={PlayCircle} title="No videos yet" description="Highlight clips and episodes will appear here once published." />
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {videos.map((v) => (
                    <button key={v.key} type="button" onClick={() => v.iframe && setVideoModal(v)} className="group overflow-hidden rounded-3xl border border-border bg-background text-left transition-shadow hover:shadow-[0_18px_50px_rgba(92,52,18,0.12)]" data-testid="landing-video-card">
                      <AspectRatio ratio={16 / 9} className="relative bg-secondary">
                        {v.thumbnail && <img src={v.thumbnail} alt={v.title || "video"} className="h-full w-full object-cover" />}
                        <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/30">
                          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-lg transition-transform duration-200 group-hover:scale-105"><PlayCircle className="h-7 w-7" /></span>
                        </span>
                      </AspectRatio>
                      <div className="p-4">
                        <p className="font-medium text-foreground">{v.title || "Untitled"}</p>
                        {v.description && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{v.description}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="photos" className="mt-6">
              {photos.length === 0 ? (
                <EmptyState icon={ImageIcon} title="No photos yet" description="Event photos will appear here once published." />
              ) : (
                <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4">
                  {photos.map((p) => (
                    <button key={p.media_id} type="button" onClick={() => setLightbox(p)} className="block w-full overflow-hidden rounded-2xl border border-border">
                      <img src={p.data || p.url} alt={p.title || "Event photo"} loading="lazy" className="w-full object-cover transition-transform duration-300 hover:scale-[1.03]" />
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Section>
      )}

      <JoinBand fridays={fridays} />

      <Footer />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl border-border bg-card p-2">
          {lightbox && <img src={lightbox.data || lightbox.url} alt={lightbox.title || ""} className="mx-auto max-h-[85vh] w-auto max-w-full rounded-lg object-contain" data-testid="landing-lightbox-image" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!videoModal} onOpenChange={(o) => !o && setVideoModal(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{videoModal?.title || "Video"}</DialogTitle></DialogHeader>
          {videoModal?.iframe && (
            <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
              <iframe src={videoModal.iframe} title={videoModal.title || "video"} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="landing-video-player" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
